import { useState, type FormEvent } from "react";
import { Button } from "@/components/ui/button";
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { useAuthContext } from "@/hooks/useAuthContext";
import { useAuthStore } from "@/stores/auth-store";
import { STRINGS } from "@/constants/strings";
import { LoaderCircleIcon } from "lucide-react";

interface ForgotPasswordPageProps {
  onBackToLogin: () => void;
}

export default function ForgotPasswordPage({
  onBackToLogin,
}: ForgotPasswordPageProps) {
  const { forgotPassword, confirmForgotPassword } = useAuthContext();
  const step = useAuthStore((s) => s.step);
  const error = useAuthStore((s) => s.error);
  const [email, setEmail] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const isConfirmStep = step === "forgot_password_confirm";

  async function handleSendCode(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const emailValue = (
      form.elements.namedItem("email") as HTMLInputElement
    ).value;
    setEmail(emailValue);

    setIsSubmitting(true);
    try {
      await forgotPassword(emailValue);
    } catch {
      // error set in store
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleConfirm(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLocalError(null);
    const form = event.currentTarget;
    const code = (form.elements.namedItem("code") as HTMLInputElement).value;
    const password = (
      form.elements.namedItem("newPassword") as HTMLInputElement
    ).value;
    const confirm = (
      form.elements.namedItem("confirmPassword") as HTMLInputElement
    ).value;

    if (password !== confirm) {
      setLocalError(STRINGS.auth.passwordMismatch);
      return;
    }

    setIsSubmitting(true);
    try {
      await confirmForgotPassword(email, code, password);
      setSuccess(true);
    } catch {
      // error set in store
    } finally {
      setIsSubmitting(false);
    }
  }

  const displayError = localError ?? error;

  if (success) {
    return (
      <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-background p-6 md:p-10">
        <div className="w-full max-w-sm">
          <FieldGroup>
            <div className="flex flex-col items-center gap-2 text-center">
              <h1 className="text-xl font-bold">
                {STRINGS.auth.forgotPasswordTitle}
              </h1>
              <FieldDescription>
                {STRINGS.auth.passwordResetSuccess}
              </FieldDescription>
            </div>
            <Field>
              <Button
                type="button"
                className="w-full"
                onClick={onBackToLogin}
              >
                {STRINGS.auth.backToLogin}
              </Button>
            </Field>
          </FieldGroup>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-background p-6 md:p-10">
      <div className="w-full max-w-sm">
        {!isConfirmStep ? (
          <form onSubmit={handleSendCode}>
            <FieldGroup>
              <div className="flex flex-col items-center gap-2 text-center">
                <h1 className="text-xl font-bold">
                  {STRINGS.auth.forgotPasswordTitle}
                </h1>
                <FieldDescription>
                  {STRINGS.auth.forgotPasswordDescription}
                </FieldDescription>
              </div>

              {displayError && (
                <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                  {displayError}
                </div>
              )}

              <Field>
                <FieldLabel htmlFor="email">
                  {STRINGS.auth.emailLabel}
                </FieldLabel>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder={STRINGS.auth.emailPlaceholder}
                  required
                  disabled={isSubmitting}
                />
              </Field>

              <Field>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <LoaderCircleIcon className="animate-spin" />
                      {STRINGS.auth.sendingCode}
                    </>
                  ) : (
                    STRINGS.auth.sendCodeButton
                  )}
                </Button>
              </Field>

              <Field>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={onBackToLogin}
                >
                  {STRINGS.auth.backToLogin}
                </Button>
              </Field>
            </FieldGroup>
          </form>
        ) : (
          <form onSubmit={handleConfirm}>
            <FieldGroup>
              <div className="flex flex-col items-center gap-2 text-center">
                <h1 className="text-xl font-bold">
                  {STRINGS.auth.confirmCodeTitle}
                </h1>
                <FieldDescription>
                  {STRINGS.auth.confirmCodeDescription}
                </FieldDescription>
              </div>

              {displayError && (
                <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                  {displayError}
                </div>
              )}

              <Field>
                <FieldLabel htmlFor="code">
                  {STRINGS.auth.codeLabel}
                </FieldLabel>
                <Input
                  id="code"
                  name="code"
                  type="text"
                  placeholder={STRINGS.auth.codePlaceholder}
                  required
                  disabled={isSubmitting}
                />
              </Field>

              <Field>
                <FieldLabel htmlFor="newPassword">
                  {STRINGS.auth.newPasswordLabel}
                </FieldLabel>
                <Input
                  id="newPassword"
                  name="newPassword"
                  type="password"
                  placeholder={STRINGS.auth.newPasswordPlaceholder}
                  autoComplete="new-password"
                  required
                  disabled={isSubmitting}
                />
              </Field>

              <Field>
                <FieldLabel htmlFor="confirmPassword">
                  {STRINGS.auth.confirmPasswordLabel}
                </FieldLabel>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  placeholder={STRINGS.auth.confirmPasswordPlaceholder}
                  autoComplete="new-password"
                  required
                  disabled={isSubmitting}
                />
              </Field>

              <Field>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <LoaderCircleIcon className="animate-spin" />
                      {STRINGS.auth.resettingPassword}
                    </>
                  ) : (
                    STRINGS.auth.resetPasswordButton
                  )}
                </Button>
              </Field>

              <Field>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={onBackToLogin}
                >
                  {STRINGS.auth.backToLogin}
                </Button>
              </Field>
            </FieldGroup>
          </form>
        )}
      </div>
    </div>
  );
}
